// Updated: 2026-08-04 18:48:00 CDT
// Purpose: Poll the public OpenF5 health endpoint and recover automatically after outages.

import { useEffect, useState } from "react";
import { createTTSClientFromEnv,TtsHealthError,type Health,type HealthResponseMetadata } from "@/lib/ttsClient";
import {reportAudioHealth,type AudioHealthEvent} from '@/utils/audioHealthDiagnostics';
import {
  classifyTtsHealth,
  type TtsAvailabilityStatus,
} from "@/utils/ttsAvailability";

const HEALTH_TIMEOUT_MS = 10_000;
const HEALTH_REFRESH_MS = 30_000;

export const useTtsAvailability = () => {
  const [status, setStatus] = useState<TtsAvailabilityStatus>("checking");

  useEffect(() => {
    let isCancelled = false;
    let refreshTimer: number | undefined;
    let activeController: AbortController | null = null;
    let incident:string|null=null,failures=0;

    const checkHealth = async () => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      const timeout = window.setTimeout(
        () => controller.abort(),
        HEALTH_TIMEOUT_MS,
      );
      const started=performance.now();let metadata:HealthResponseMetadata|undefined,health:Health|undefined;
      const record=(kind:AudioHealthEvent['kind'],error?:unknown)=>{
        incident??=crypto.randomUUID();
        void reportAudioHealth({version:1,incident_id:incident,kind,observed_at:new Date().toISOString(),elapsed_ms:Math.round(performance.now()-started),
          timeout_ms:HEALTH_TIMEOUT_MS,failure_count:failures,http_status:metadata?.http_status??0,visibility:document.hidden?'hidden':'visible',online:navigator.onLine,
          exception_name:(error instanceof Error||error instanceof DOMException)&&/^[A-Za-z][A-Za-z0-9]{0,79}$/.test(error.name)?error.name:'',
          ...(metadata?{response:Object.fromEntries(Object.entries(metadata).filter(([k])=>k!=='http_status'))}:{}),
          ...(health?{health:{status:health.status==='ok'?'ok':'error',model_loaded:health.model_loaded===true,model_loading:health.model_loading===true,load_error:health.load_error===true}}:{})});
      };

      try {
        const client = createTTSClientFromEnv();
        health = await client.health({ signal: controller.signal,onResponse:value=>{metadata=value;} });

        if (!isCancelled) {
          const nextStatus = classifyTtsHealth(health);
          setStatus(nextStatus);
          if(nextStatus!=='online'){failures++;record('unhealthy');}
          else if(incident){record('recovered');incident=null;failures=0;}
          else void reportAudioHealth();
          console.debug("[useTtsAvailability] TTS health refreshed:", {
            status: nextStatus,
            modelLoaded: health.model_loaded,
            modelLoading: health.model_loading,
            loadError: health.load_error,
          });
        }
      } catch (error) {
        if (!isCancelled) {
          if(error instanceof TtsHealthError)metadata=error.diagnostic;
          failures++;record(controller.signal.aborted?'timeout':metadata&&metadata.http_status>=400?'http':'network',error);
          setStatus("offline");
          console.error("[useTtsAvailability] TTS health check failed:", error);
        }
      } finally {
        window.clearTimeout(timeout);

        if (!isCancelled) {
          refreshTimer = window.setTimeout(checkHealth, failures?Math.min(HEALTH_REFRESH_MS,5000*failures):HEALTH_REFRESH_MS);
        }
      }
    };

    void checkHealth();

    return () => {
      isCancelled = true;
      activeController?.abort();
      if (refreshTimer !== undefined) {
        window.clearTimeout(refreshTimer);
      }
    };
  }, []);

  return status;
};
